/* Form-family park: the time period boundaries convert with the Table round (component-map.md). */
import { FormControl, FormLabel, Stack } from '@chakra-ui/react';
import * as tp from '@main/shared/utils/timeperiod';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from '../combobox';
import { TimeBoundaryAbsolute } from './time-boundary-absolute';
import { TimeBoundaryRelative } from './time-boundary-relative';

export type TimeBoundaryProps = {
  label: string;
  value?: tp.TimeBoundary;
  onChange: (value: tp.TimeBoundary) => void;
  isDisabled?: boolean;
};

export const TimeBoundary = ({ label, value, onChange, isDisabled }: TimeBoundaryProps) => {
  const { t } = useTranslation('ui');

  const boundaryTypeOptions = useMemo(() => {
    return Object.values(tp.TimeBoundaryType).map((value) => ({
      value,
      label: t(`timePeriod.boundaryType.${value}`),
    }));
  }, [t]);

  return (
    <Stack>
      <FormControl>
        <FormLabel>{label}</FormLabel>
        <Select
          value={boundaryTypeOptions.find((option) => option.value === value?.type)}
          options={boundaryTypeOptions}
          onChange={(boundaryType) => {
            if (boundaryType) {
              onChange(createDefaultTimeBoundary(boundaryType.value));
            }
          }}
          useBasicStyles
          isDisabled={isDisabled}
        />
      </FormControl>
      {value?.type === tp.TimeBoundaryType.Absolute && (
        <TimeBoundaryAbsolute value={value} onChange={onChange} isDisabled={isDisabled} />
      )}
      {value?.type === tp.TimeBoundaryType.Relative && (
        <TimeBoundaryRelative value={value} onChange={onChange} isDisabled={isDisabled} />
      )}
    </Stack>
  );
};

function createDefaultTimeBoundary(type: tp.TimeBoundaryType): tp.TimeBoundary {
  switch (type) {
    case tp.TimeBoundaryType.Now:
      return { type };

    case tp.TimeBoundaryType.Absolute:
      return {
        type,
        timestamp: new Date(),
      };

    case tp.TimeBoundaryType.Relative:
      return {
        type,
        unit: tp.TimePeriodUnit.Day,
        offset: 0,
        alignment: tp.TimePeriodAlignment.Start,
      };
  }
}
